import { BaseAssetManager, type AssetNamespace } from '@gotti/core';
import type { AssetWriteBackend } from '@gotti/core';
import { MasterReadSource, type MasterAssetTransport } from './MasterAssetTransport.js';

export type { MasterAssetTransport } from './MasterAssetTransport.js';

function splitNs(fullPath: string): { namespace: AssetNamespace; path: string } {
  const idx = fullPath.indexOf('/');
  return { namespace: fullPath.slice(0, idx) as AssetNamespace, path: fullPath.slice(idx + 1) };
}

/**
 * Server-side asset cache + relay. The master owns the bytes: `save`/`remove`
 * relay to the master (which persists, audits, and fans the change back out);
 * cold reads fetch from the master via the transport. Author hooks are NOT
 * fired here — they fire when the fan-out ASSET_CHANGED lands (applyRemoteChange,
 * inherited from BaseAssetManager), so every process (including the writer)
 * fires them the same way.
 */
export class ServerAssetManager extends BaseAssetManager implements AssetWriteBackend {
  constructor(private readonly transport: MasterAssetTransport) {
    super(new MasterReadSource(transport));
  }

  async save(fullPath: string, bytes: Uint8Array, mimeType: string): Promise<void> {
    // Optimistic local cache so this process's own read-after-write is coherent
    // before the master's fan-out returns.
    this.cacheSet(fullPath, { path: fullPath, mimeType, bytes });
    const { namespace, path } = splitNs(fullPath);
    try {
      await this.transport.write('save', { namespace, path, bytes });
    } catch (e) {
      // Relay failed — bytes are NOT persisted at the master. Drop the optimistic
      // entry so the next load re-fetches truth instead of serving an un-persisted write.
      this.cacheDelete(fullPath);
      throw e;
    }
  }

  async remove(fullPath: string): Promise<void> {
    this.cacheDelete(fullPath);
    const { namespace, path } = splitNs(fullPath);
    await this.transport.write('remove', { namespace, path });
  }
}
